import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { Plus, Search } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useAdrsForCanvas } from '../../stores';
import type { ADR } from '../../types';
import { AdrModal } from '../../adr/components/AdrModal';
import { blocksToText } from '../../lib/blocksToText';
import { STATUS_COLORS, IMPL_STATUS_COLORS } from '../../adr/adrColors';

// ============================================
// AdrSidebarPanel
// ============================================

export const AdrSidebarPanel = memo(function AdrSidebarPanel() {
  const { t } = useTranslation();
  const { store, uiStore, boardId } = useCanvasStores();
  const canvasId = useSelector(store, s => s.context.canvas.id ?? '');
  const adrs = useAdrsForCanvas(boardId, canvasId);
  const activeAdrId = useSelector(uiStore, s => s.context.adrDetailAdrId);
  const [searchText, setSearchText] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const filteredAdrs = useMemo(() => {
    if (!searchText) return adrs;
    const lower = searchText.toLowerCase();
    return adrs.filter(a => {
      if (a.title.toLowerCase().includes(lower)) return true;
      if (a.id.toLowerCase().includes(lower)) return true;
      if (a.status.toLowerCase().includes(lower)) return true;
      if (blocksToText(a.context).includes(lower)) return true;
      if (blocksToText(a.decision).includes(lower)) return true;
      if (blocksToText(a.consequences).includes(lower)) return true;
      return false;
    });
  }, [adrs, searchText]);

  // Group by scope
  const grouped = useMemo(() => {
    const global: ADR[] = [];
    const board: ADR[] = [];
    const canvas: ADR[] = [];
    for (const adr of filteredAdrs) {
      if (adr.scope.type === 'global') global.push(adr);
      else if (adr.scope.type === 'board') board.push(adr);
      else canvas.push(adr);
    }
    return { global, board, canvas };
  }, [filteredAdrs]);

  const handleOpenNew = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((adrId: string) => {
    uiStore.send({ type: 'adrDetail.open', adrId });
  }, [uiStore]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, adrId: string) => {
    e.dataTransfer.setData('application/x-canvas-adr-trigger', JSON.stringify({ adrIds: [adrId] }));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const defaultScope: ADR['scope'] = canvasId
    ? { type: 'canvas', boardId, treeId: canvasId }
    : { type: 'board', boardId };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Search + Add */}
      <div style={{ padding: '6px 8px', display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={10} style={{ position: 'absolute', left: 6, top: 6, color: '#94a3b8' }} />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('adr.sidebar.search')}
            style={{
              width: '100%', fontSize: 10, padding: '3px 6px 3px 20px',
              border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none',
              backgroundColor: '#fff', color: '#1f2937',
            }}
          />
        </div>
        <button
          onClick={handleOpenNew}
          title={t('adr.sidebar.newAdr')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, border: '1px solid #e2e8f0', borderRadius: 4,
            background: '#fff', cursor: 'pointer', color: '#3b82f6',
          }}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
        {filteredAdrs.length === 0 ? (
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', padding: 16 }}>
            {t('adr.sidebar.noResults')}
          </div>
        ) : (
          <>
            {grouped.global.length > 0 && (
              <ScopeGroup label={t('adr.sidebar.global')} adrs={grouped.global} activeAdrId={activeAdrId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
            {grouped.board.length > 0 && (
              <ScopeGroup label={t('adr.sidebar.board')} adrs={grouped.board} activeAdrId={activeAdrId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
            {grouped.canvas.length > 0 && (
              <ScopeGroup label={t('adr.sidebar.canvas')} adrs={grouped.canvas} activeAdrId={activeAdrId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
          </>
        )}
      </div>

      {/* Modal — only for creating new ADRs */}
      <AdrModal
        isOpen={createModalOpen}
        onClose={handleCloseCreateModal}
        defaultScope={defaultScope}
      />
    </div>
  );
});

// ============================================
// ScopeGroup
// ============================================

function ScopeGroup({
  label,
  adrs,
  activeAdrId,
  onOpen,
  onDragStart,
}: {
  label: string;
  adrs: ADR[];
  activeAdrId: string | null;
  onOpen: (adrId: string) => void;
  onDragStart: (e: React.DragEvent, adrId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      {adrs.map(adr => {
        const isActive = adr.id === activeAdrId;
        return (
        <div
          key={adr.id}
          draggable
          onDragStart={(e) => onDragStart(e, adr.id)}
          onClick={() => onOpen(adr.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
            borderRadius: 4, cursor: 'pointer', marginBottom: 1,
            backgroundColor: isActive ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {/* Status dot */}
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: STATUS_COLORS[adr.status],
              flexShrink: 0,
            }}
          />
          {/* Title */}
          <span style={{ fontSize: 10, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {adr.title}
          </span>
          {/* Implementation badge */}
          <span
            style={{
              fontSize: 8, padding: '1px 4px', borderRadius: 3,
              backgroundColor: IMPL_STATUS_COLORS[adr.implementationStatus] + '20',
              color: IMPL_STATUS_COLORS[adr.implementationStatus],
              fontWeight: 500, flexShrink: 0,
            }}
          >
            {t(`adr.implStatus.${adr.implementationStatus}` as any)}
          </span>
        </div>
        );
      })}
    </div>
  );
}
